import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { htmlToADF } from '@atlassian/html-adf-transformer';

import type {
	CreateBulkIssuesResponse,
	CreateIssueBodyIssueLinkType,
	FieldMetadata,
	GICCompatibleObject,
	JiraComponents,
	JiraPriorities,
	JiraVersions,
	SupportedValueType,
} from '../__types__/apiUtils';
import {
	FieldMetadataSchemaCustom,
	FieldMetadataSchemaItems,
	FieldMetadataSchemaSystem,
	FieldMetadataSchemaType,
	isSelectOptionList,
	isIdNameObjectList,
	FieldMetadataSchemaOperations,
} from '../__types__/apiUtils';
import { type IssueObject } from '../providers/BulkCreateContextProvider';
import type {
	Column,
	ColumnToFieldMapping,
	ValidatedIssueFieldValues,
} from '../providers/BulkConfigureContextProvider';
import { FieldType } from '../providers/BulkConfigureContextProvider';
import type { BulkConfigureValidationOptions } from '../__types__/tableUtils';

interface AdfNode {
	type: string;
	text?: string;
	attrs?: any;
	marks?: Array<AdfNode>;
	content?: Array<AdfNode | undefined>;
}

const TEAM_ID_PATTERN =
	/[0-9A-Za-z]{8}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{12}/;

const getFieldTypeFromMapping = (mapping: ColumnToFieldMapping): FieldType => {
	if (mapping.field) {
		return getFieldTypeFromMetadata(mapping.field);
	}

	return FieldType.UNSUPPORTED;
};

export const getFieldTypeFromMetadata = (metadata: FieldMetadata): FieldType => {
	switch (metadata.schema.type) {
		// "SHORT_TEXT" is the least restrictive type, so we can use it for "ANY"
		case FieldMetadataSchemaType.STRING:
			const customType = metadata.schema.custom;
			return customType === FieldMetadataSchemaCustom.TEXT_AREA
				? FieldType.PARAGRAPH
				: FieldType.SHORT_TEXT;
		case FieldMetadataSchemaType.NUMBER:
			return FieldType.NUMBER;
		case FieldMetadataSchemaType.DATE:
			return FieldType.DATE;
		case FieldMetadataSchemaType.USER:
			return FieldType.USER;
		case FieldMetadataSchemaType.PRIORITY:
			return FieldType.PRIORITY;
		case FieldMetadataSchemaType.DATETIME:
			return FieldType.TIMESTAMP;
		case FieldMetadataSchemaType.ARRAY:
			if (metadata.schema.custom === FieldMetadataSchemaCustom.PEOPLE) {
				return FieldType.PEOPLE;
			} else if (metadata.schema.system === FieldMetadataSchemaSystem.LABELS) {
				return FieldType.LABELS;
			} else if (metadata.schema.items === FieldMetadataSchemaItems.VERSION) {
				return FieldType.VERSIONS;
			} else if (
				metadata.schema.items === FieldMetadataSchemaItems.OPTION &&
				metadata.operations?.includes(FieldMetadataSchemaOperations.ADD)
			) {
				return FieldType.MULTI_SELECT;
			} else if (
				metadata.schema.items === FieldMetadataSchemaItems.OPTION &&
				metadata.operations?.includes(FieldMetadataSchemaOperations.SET)
			) {
				return FieldType.SINGLE_SELECT;
			}

			return FieldType.UNSUPPORTED;
		case FieldMetadataSchemaType.TEAM:
			return FieldType.TEAM;
		case FieldMetadataSchemaType.ISSUELINK:
			if (metadata.schema.system === FieldMetadataSchemaSystem.PARENT) {
				return FieldType.PARENT;
			}
			return FieldType.UNSUPPORTED;
		case FieldMetadataSchemaType.COMPONENTS:
			// NOTE: Components are not a standard field type.
			return FieldType.COMPONENTS;
		default:
			return FieldType.UNSUPPORTED;
	}
};

const getMentionAccountIdFromAdfNode = (adfNode: AdfNode): string | null => {
	return adfNode.type == 'mention' && adfNode.attrs?.id ? adfNode.attrs.id : null;
};

const getMentionAccountIdsFromAdfTree = (adf: DocNode): Array<string> => {
	const nodes: Array<AdfNode> = [adf];
	const accountIds: Array<string> = [];

	let node: AdfNode | undefined;
	while ((node = nodes.pop()) !== undefined) {
		const accountId = getMentionAccountIdFromAdfNode(node);
		accountId && accountIds.push(node.attrs.id);

		node.content?.forEach((n) => {
			n && nodes.push(n);
		});
	}

	return accountIds;
};

const getTeamIdFromUrl = (urlString?: string) => {
	if (!urlString) {
		return null;
	}

	let url;
	try {
		url = new URL(urlString);
	} catch {
		return null;
	}

	const path = url.pathname;
	const pathParts = path.split('/');
	if (path.startsWith('/people/team') && pathParts.length > 3) {
		const teamId = pathParts[3];
		return teamId.match(TEAM_ID_PATTERN) ? teamId : null;
	}

	return null;
};

const getTeamIdFromAdfNode = (adfNode: AdfNode): string | null => {
	let urlString;
	if (adfNode.type == 'inlineCard' || adfNode.type == 'blockCard') {
		urlString = adfNode.attrs?.url;
	} else if (adfNode.type == 'link') {
		urlString = adfNode.attrs?.href;
	} else if (adfNode.type == 'text') {
		urlString = adfNode.text;
	}

	return getTeamIdFromUrl(urlString);
};

const getTeamIdFromAdfTree = (adf: DocNode) => {
	const nodes: Array<AdfNode> = [adf];

	let node: AdfNode | undefined;
	while ((node = nodes.pop()) !== undefined) {
		const teamId = getTeamIdFromAdfNode(node);
		if (teamId) {
			return teamId;
		}

		node.content?.forEach((n) => {
			n && nodes.push(n);
		});

		node.marks?.forEach((n) => {
			nodes.push(n);
		});
	}

	return null;
};

export const getSpecialArrayFromAdfTree = (adf: DocNode, delimeterRegex = /\s+/): Array<string> => {
	const nodes: Array<AdfNode> = [adf];
	const array: Array<string> = [];

	let node: AdfNode | undefined;
	while ((node = nodes.pop()) !== undefined) {
		if (node.type == 'text' && node.text) {
			array.push(...node.text.split(delimeterRegex));
		}

		node.content?.forEach((n) => {
			n && nodes.push(n);
		});
	}

	return array;
};

export const getIssueFieldValueFromCellValue = (
	fieldType: FieldType,
	fieldId: string,
	cell: (Node & HTMLElement) | null,
	options: BulkConfigureValidationOptions,
	fieldMetadata?: FieldMetadata,
): SupportedValueType | null => {
	if (!cell) {
		return null;
	}
	const textContent = cell.textContent?.trim();
	const adf = htmlToADF(cell.innerHTML);
	let accountIds: Array<string>;

	const dateFromTextContent = textContent ? new Date(textContent) : null;
	const validDateOrNull =
		dateFromTextContent && !isNaN(dateFromTextContent.getTime()) ? dateFromTextContent : null;

	switch (fieldType) {
		case FieldType.SHORT_TEXT:
			return textContent;
		case FieldType.PARAGRAPH:
			return adf;
		case FieldType.NUMBER:
			return Number(textContent);
		case FieldType.DATE:
			return validDateOrNull ? validDateOrNull.toISOString().split('T')[0] : null;
		case FieldType.USER:
			accountIds = getMentionAccountIdsFromAdfTree(adf);
			return accountIds.length > 0 ? { id: accountIds[0] } : null;
		case FieldType.PRIORITY:
			const prioritiesAllowedValues = options.mappingFieldAllowedValues.get(fieldId);
			if (!isIdNameObjectList(prioritiesAllowedValues)) {
				return null;
			}
			// Priorities cannot have the same name and are case-insensitive, so we can do a lenient match.
			const lenientPriority = findLenientMatches(
				textContent,
				prioritiesAllowedValues as JiraPriorities,
			);
			return lenientPriority.length > 0
				? { name: lenientPriority[0].name, id: lenientPriority[0].id }
				: null;
		case FieldType.COMPONENTS:
			const componentsAllowedValues = options.mappingFieldAllowedValues.get(fieldId);

			if (!isIdNameObjectList(componentsAllowedValues)) {
				return null;
			}

			const lenientComponents = findLenientMatches(
				textContent,
				componentsAllowedValues as JiraComponents,
				/\n+/,
				adf,
			);

			return lenientComponents.length > 0
				? lenientComponents.map((component) => ({ id: component.id }))
				: null;
		case FieldType.VERSIONS:
			const versionsAllowedValues = options.mappingFieldAllowedValues.get(fieldId);
			if (!isIdNameObjectList(versionsAllowedValues)) {
				return null;
			}

			const lenientVersions = findLenientMatches(
				textContent,
				versionsAllowedValues as JiraVersions,
				/\n+/,
				adf,
			);
			return lenientVersions.length > 0
				? lenientVersions.map((version) => ({
						id: version.id,
					}))
				: null;
		case FieldType.SINGLE_SELECT:
		case FieldType.MULTI_SELECT:
			const selectAllowedValues = options.mappingFieldAllowedValues.get(fieldId);
			if (!isSelectOptionList(selectAllowedValues)) {
				return null;
			}

			// Convert the allowedValues to the format expected by findLenientMatches
			const lenientOptions = findLenientMatches(
				textContent,
				selectAllowedValues.map((option) => ({
					name: option.value,
					id: option.id,
				})),
				/\n+/,
				adf,
			);

			return lenientOptions.length > 0 ? lenientOptions : null;
		case FieldType.TIMESTAMP:
			return validDateOrNull ? validDateOrNull.toISOString() : null;
		case FieldType.PEOPLE:
			accountIds = getMentionAccountIdsFromAdfTree(adf);
			return accountIds.length > 0 ? accountIds.map((id) => ({ id })) : null;
		case FieldType.LABELS:
			return getSpecialArrayFromAdfTree(adf);
		case FieldType.TEAM:
			return getTeamIdFromAdfTree(adf);
		case FieldType.PARENT:
			if (textContent == null) {
				return null;
			}

			const trimmedKey = textContent.toUpperCase().trim();
			const mappedType = options.mappingValidParentCandidates?.get(trimmedKey);

			return mappedType ? { key: mappedType.key, id: mappedType.issueId } : undefined;
		default:
			// Check for field-specific allowedValues validation for custom fields
			if (fieldMetadata && fieldMetadata.allowedValues && options.mappingFieldAllowedValues) {
				const fieldValidationResult = validateFieldWithAllowedValues(
					textContent,
					fieldMetadata.fieldId,
					options,
				);
				if (fieldValidationResult) {
					return fieldValidationResult;
				}
			}
			return textContent;
	}
};

const findLenientMatches = <T extends { name: string; id: string }>(
	textContent: string | null | undefined,
	availableItems: T[] | undefined,
	delimiterRegex: RegExp = /\n+/,
	adf?: DocNode,
): T[] => {
	if (!textContent || !availableItems) {
		return [];
	}

	const searchTexts = adf ? getSpecialArrayFromAdfTree(adf, delimiterRegex) : [textContent];

	return searchTexts
		.map((searchText) =>
			availableItems.find(
				(item) => item.name.trim().toLowerCase() === searchText.trim().toLowerCase(),
			),
		)
		.filter((item): item is T => item !== undefined);
};

// Flexible function to find text matches in allowedValues with different structures
// Supports various allowedValues formats:
// - { name: string, id: string } (priorities)
// - { name: string, value: string } (custom dropdowns)
// - { displayName: string, value: string } (some custom fields)
const findFlexibleMatches = (textContent: string, allowedValues: unknown[]): unknown[] => {
	if (!textContent || !allowedValues) {
		return [];
	}

	const searchText = textContent.trim().toLowerCase();

	return allowedValues.filter((item) => {
		if (!item || typeof item !== 'object') {
			return false;
		}

		const obj = item as Record<string, any>;

		// Try different property names that might contain the display text
		// Order matters: more specific names first
		const possibleTextProperties = ['name', 'displayName', 'value'];

		for (const prop of possibleTextProperties) {
			if (obj[prop] && typeof obj[prop] === 'string') {
				if (obj[prop].trim().toLowerCase() === searchText) {
					return true;
				}
			}
		}

		return false;
	});
};

const validateFieldWithAllowedValues = (
	textContent: string | null | undefined,
	fieldId: string,
	options?: BulkConfigureValidationOptions,
): any | null => {
	if (!textContent || !fieldId) {
		return null;
	}

	// Look up field-specific allowedValues
	const fieldAllowedValues = options?.mappingFieldAllowedValues?.get(fieldId);

	if (fieldAllowedValues && Array.isArray(fieldAllowedValues)) {
		const matches = findFlexibleMatches(textContent, fieldAllowedValues);
		return matches.length > 0 ? matches[0] : null;
	}

	return null;
};

// Empty rows are not included in the issue create list, so we should not include them in the validation
const rowIsEmpty = (row: HTMLTableRowElement) => {
	return Array.from(row.cells).every((cell) => cell.textContent?.trim() === '');
};

export const isHeaderRowCells = (cells: Array<any>): boolean => {
	if (!Array.isArray(cells) || cells.length === 0) {
		return false;
	}
	const first = cells[0] as any;
	// Address HTML table cells
	if (first && typeof first === 'object' && 'tagName' in first) {
		return (cells as Array<HTMLElement>).every(
			(cell) =>
				(cell as any).tagName?.toLowerCase() === 'th' ||
				(cell as any).classList?.contains('ak-renderer-table-number-column'),
		);
	}
	// Address ADF cell nodes
	return cells.every((c: any) => c?.type === 'tableHeader');
};

function adjustInsertionColumn(
	rows: ADFEntity[],
	targetRows: Map<number, string>,
	startIndex: number = 0,
): number {
	const firstTargetRow = [...targetRows.keys()][0];

	const row = rows[firstTargetRow];
	if (!row || row.type !== 'tableRow' || !row.content) {
		return -1;
	}

	const firstTableCellIndex = row.content
		.slice(startIndex)
		.findIndex((cell) => cell?.type === 'tableCell');
	if (firstTableCellIndex < 0) {
		return -1;
	}
	return startIndex + firstTableCellIndex;
}

export const getValidatedIssueFieldValuesForType = (
	fieldType: FieldType,
	fieldId: string,
	column: Column,
	table: HTMLTableElement,
	validator: (value: SupportedValueType | null) => boolean,
	options: BulkConfigureValidationOptions,
	fieldMetadata?: FieldMetadata,
): ValidatedIssueFieldValues | null => {
	const rows = Array.from(table.rows).slice(1);
	// Ignore completely empty rows for validation purposes
	const nonEmptyRows = rows.filter((row) => !rowIsEmpty(row));
	const totalRowCount = nonEmptyRows.length;

	if (totalRowCount === 0) {
		return null;
	}

	// Get all field values from non-empty rows (ignore empty rows entirely)
	const allFieldValues = nonEmptyRows.map((row) =>
		getIssueFieldValueFromCellValue(
			fieldType,
			fieldId,
			row.cells[column.index],
			options,
			fieldMetadata,
		),
	);

	// Filter out null values and apply validator for the final fieldValues array
	const fieldValues = allFieldValues.filter(
		(value): value is SupportedValueType => value !== null && validator(value),
	);

	// Count how many of the ALL original values (including nulls) are valid
	const validValues = allFieldValues.filter(validator);

	// If no valid values, return null
	if (validValues.length === 0) {
		return null;
	}

	return {
		canBeRequired: validValues.length === totalRowCount,
		fieldValues,
	};
};

export const getValidatedIssueFieldValuesForColumn = (
	column: Column,
	fieldId: string,
	table: HTMLTableElement,
	options: BulkConfigureValidationOptions,
) => {
	const validatedIssueFieldValues: Map<FieldType, ValidatedIssueFieldValues> = new Map();

	const setIfNotNull = (
		fieldType: FieldType,
		validatedValues: ValidatedIssueFieldValues | null,
	) => {
		if (validatedValues) {
			validatedIssueFieldValues.set(fieldType, validatedValues);
		}
	};

	const dateValidator = (value: SupportedValueType | null) =>
		value != null && !isNaN(new Date(value.toString()).getTime());

	const userValidator = (value: SupportedValueType | null) => value != null;

	const textValidator = (value: SupportedValueType | null) =>
		value != null && typeof value === 'string' && value.trim().length > 0;

	const adfValidator = (value: SupportedValueType | null) =>
		value != null && typeof value === 'object';

	const shortTextResult = getValidatedIssueFieldValuesForType(
		FieldType.SHORT_TEXT,
		fieldId,
		column,
		table,
		textValidator,
		options,
	);

	setIfNotNull(FieldType.SHORT_TEXT, shortTextResult);

	const paragraphResult = getValidatedIssueFieldValuesForType(
		FieldType.PARAGRAPH,
		fieldId,
		column,
		table,
		adfValidator,
		options,
	);

	setIfNotNull(FieldType.PARAGRAPH, paragraphResult);

	const numberResult = getValidatedIssueFieldValuesForType(
		FieldType.NUMBER,
		fieldId,
		column,
		table,
		(value: SupportedValueType | null) => value != null && !Number.isNaN(Number(value)),
		options,
	);

	setIfNotNull(FieldType.NUMBER, numberResult);

	const dateResult = getValidatedIssueFieldValuesForType(
		FieldType.DATE,
		fieldId,
		column,
		table,
		dateValidator,
		options,
	);

	setIfNotNull(FieldType.DATE, dateResult);

	const timestampResult = getValidatedIssueFieldValuesForType(
		FieldType.TIMESTAMP,
		fieldId,
		column,
		table,
		dateValidator,
		options,
	);

	setIfNotNull(FieldType.TIMESTAMP, timestampResult);

	const userResult = getValidatedIssueFieldValuesForType(
		FieldType.USER,
		fieldId,
		column,
		table,
		userValidator,
		options,
	);

	setIfNotNull(FieldType.USER, userResult);

	const priorityResult = getValidatedIssueFieldValuesForType(
		FieldType.PRIORITY,
		fieldId,
		column,
		table,
		(value) => value != null,
		options,
	);

	setIfNotNull(FieldType.PRIORITY, priorityResult);

	const componentResult = getValidatedIssueFieldValuesForType(
		FieldType.COMPONENTS,
		fieldId,
		column,
		table,
		(value) => value != null,
		options,
	);

	setIfNotNull(FieldType.COMPONENTS, componentResult);

	const versionsResult = getValidatedIssueFieldValuesForType(
		FieldType.VERSIONS,
		fieldId,
		column,
		table,
		(value) => value != null,
		options,
	);

	setIfNotNull(FieldType.VERSIONS, versionsResult);

	const peopleResult = getValidatedIssueFieldValuesForType(
		FieldType.PEOPLE,
		fieldId,
		column,
		table,
		(values) => Array.isArray(values) && values.every(userValidator),
		options,
	);

	setIfNotNull(FieldType.PEOPLE, peopleResult);

	const teamResult = getValidatedIssueFieldValuesForType(
		FieldType.TEAM,
		fieldId,
		column,
		table,
		(value: SupportedValueType | null) => !!value?.toString().match(TEAM_ID_PATTERN),
		options,
	);

	setIfNotNull(FieldType.TEAM, teamResult);

	const labelsResult = getValidatedIssueFieldValuesForType(
		FieldType.LABELS,
		fieldId,
		column,
		table,
		(value) => value != null,
		options,
	);

	setIfNotNull(FieldType.LABELS, labelsResult);

	const parentResult = getValidatedIssueFieldValuesForType(
		FieldType.PARENT,
		fieldId,
		column,
		table,
		(value) => value != null,
		options,
	);

	setIfNotNull(FieldType.PARENT, parentResult);

	const singleSelectResult = getValidatedIssueFieldValuesForType(
		FieldType.SINGLE_SELECT,
		fieldId,
		column,
		table,
		(value) => value != null && Array.isArray(value) && value.length == 1,
		options,
	);

	setIfNotNull(FieldType.SINGLE_SELECT, singleSelectResult);

	const multiSelectResult = getValidatedIssueFieldValuesForType(
		FieldType.MULTI_SELECT,
		fieldId,
		column,
		table,
		(value) => value != null && Array.isArray(value) && value.length > 0,
		options,
	);

	setIfNotNull(FieldType.MULTI_SELECT, multiSelectResult);

	return validatedIssueFieldValues;
};

export const getClosestTableFromSelection = (activeSelection: Selection | undefined | null) => {
	if (activeSelection && activeSelection.rangeCount > 0) {
		// Get the first range from the selection
		const range = activeSelection.getRangeAt(0);
		// Start from the common ancestor container of the range
		let node: Node | null = range.commonAncestorContainer;
		// If the node is a text node, move up to its parent element
		if (node?.nodeType === Node.TEXT_NODE) {
			node = node.parentElement;
		}
		// Traverse up the DOM tree to find the closest <table> element
		while (node && node.nodeType === Node.ELEMENT_NODE) {
			if ((node as HTMLElement).tagName === 'TABLE') {
				return node as HTMLTableElement;
			}
			node = node.parentElement;
		}
	}
	return null;
};

/**
 * Get the number of rows (minus the header row) that have text content
 * @param rows - The rows of the table
 * @returns The number of rows (minus the header row) that have text content
 */
export const getRowCountWithContent = (rows: HTMLCollectionOf<HTMLTableRowElement>) => {
	return Array.from(rows)
		.slice(1)
		.filter((row) => Array.from(row.cells).some((cell) => cell.textContent?.trim())).length;
};

export const mapTableToIssues = (
	mappings: ColumnToFieldMapping[],
	table: HTMLTableElement,
	options: BulkConfigureValidationOptions,
) => {
	let issueIndex = 0;
	const rows = table.rows;
	const issues: IssueObject[] = [];

	const isFirstRowHeader = rows.length > 0 && isHeaderRowCells(Array.from(rows[0].cells));
	// Starting from the second row as we will be using the first row as the header even if it is not a HTML header row
	for (let i = 1; i < rows.length; i++) {
		const issue: IssueObject = {
			id: '',
			summary: '',
			description: undefined,
			issueType: {
				iconUrl: '',
				name: '',
				id: '',
			},
			// Note: the issue insertion API does not count the HTML header row as the first row so we want to shift the index by 1 if the first row is a HTML header row
			rowIndex: i - (isFirstRowHeader ? 1 : 0),
		};
		let emptySummary = true;
		let emptyDescription = true;
		for (const mapping of mappings) {
			const textContent = mapping.column
				? rows[i]?.cells[mapping.column.index]?.textContent?.trim()
				: '';
			if (mapping.field) {
				if (mapping.field.fieldId === 'summary') {
					issue.summary = textContent || 'Untitled';
					emptySummary = !textContent;
				} else if (mapping.field.fieldId === 'description') {
					//The description expects ADF content
					const cellHTML = mapping.column ? rows[i]?.cells[mapping.column.index]?.innerHTML : '';
					issue.description = htmlToADF(cellHTML);
					emptyDescription = !textContent;
				} else if (mapping.field.fieldId === 'assignee') {
					const assigneeValue = getIssueFieldValueFromCellValue(
						FieldType.USER,
						mapping.field.fieldId,
						mapping.column ? rows[i]?.cells[mapping.column.index] : null,
						options,
						mapping.field,
					) as GICCompatibleObject | null;
					if (assigneeValue) {
						issue.assignee = assigneeValue.id;
					}
				} else if (mapping.field.fieldId === 'labels') {
					issue.labels = (getIssueFieldValueFromCellValue(
						FieldType.LABELS,
						mapping.field.fieldId,
						mapping.column ? rows[i]?.cells[mapping.column.index] : null,
						options,
						mapping.field,
					) ?? []) as string[];
				} else if (mapping.field.fieldId === 'parent') {
					const parentValue = getIssueFieldValueFromCellValue(
						FieldType.PARENT,
						mapping.field.fieldId,
						mapping.column ? rows[i]?.cells[mapping.column.index] : null,
						options,
						mapping.field,
					) as CreateIssueBodyIssueLinkType | null;
					if (parentValue) {
						issue.parent = parentValue;
					}
				} else {
					const fieldType: FieldType = getFieldTypeFromMapping(mapping);
					const fieldValue: SupportedValueType | null = getIssueFieldValueFromCellValue(
						fieldType,
						mapping.field.fieldId,
						mapping.column ? rows[i]?.cells[mapping.column.index] : null,
						options,
						mapping.field,
					);

					if (!issue.additionalFields) {
						issue.additionalFields = [];
					}
					if (fieldValue !== null) {
						issue.additionalFields.push({ field: mapping.field, value: fieldValue });
					}
				}
			}
		}
		if (!emptySummary && !emptyDescription) {
			issue.id = String(issueIndex++);
			issues.push(issue as IssueObject);
		}
	}
	return issues;
};

export const updateIssuesWithRowIndex = (
	result: CreateBulkIssuesResponse,
	rowIndices: number[],
) => {
	const failedElementNumbers = result.errors.map((error) => error.failedElementNumber);
	const updatedRowIndices = rowIndices.filter(
		(_, elementNumber) => !failedElementNumbers.includes(elementNumber),
	);
	return result.issues.map((issue, index) => {
		return {
			...issue,
			rowIndex: updatedRowIndices[index],
		};
	});
};

const issueKeyRegex = /^[A-Z][A-Z0-9_]+-[1-9][0-9]*$/;

// Returns a map of column index to array of issue type IDs found in that column
export const getJiraWorkItemKeysFromTable = (table: HTMLTableElement): Map<number, string[]> => {
	const result: Map<number, string[]> = new Map();

	// Iterate through each row in the table
	for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
		const row = table.rows[rowIndex];

		// Iterate through each cell in the row
		for (let colIndex = 0; colIndex < row.cells.length; colIndex++) {
			const cell = row.cells[colIndex];
			const textContent = cell.textContent || '';

			const matches = textContent.trim().toUpperCase().match(issueKeyRegex);

			if (matches) {
				result.set(
					colIndex,
					(result.get(colIndex) ?? []).concat(matches.map((key) => key.trim().toUpperCase())),
				);
			}
		}
	}

	return result;
};

/**
 * Constructs an updated table cell ADF node with a work item appended
 *
 * @param tableCellAdf - ADF table cell node
 * @param workItemUrl - Work item URL to insert
 * @returns Updated table cell ADF node or null if conversion fails
 */
export const constructTableCellWithWorkItem = (
	tableCellAdf: ADFEntity,
	workItemUrl: string,
): ADFEntity | null => {
	try {
		const cellContent = tableCellAdf.content ?? [];

		const workItemNode = {
			type: 'inlineCard',
			attrs: {
				url: workItemUrl,
			},
		};

		const updatedContent = [
			...cellContent,
			{
				type: 'paragraph',
				content: [workItemNode],
			},
		];

		return {
			type: tableCellAdf.type,
			attrs: tableCellAdf.attrs ?? {},
			content: updatedContent,
		};
	} catch (error) {
		return null;
	}
};

/**
 * Returns updated table rows with work items inserted into the specified column in ADF
 *
 * @param rows - Array of ADF tableRow nodes
 * @param workItemsByRow - Map of row indices to work item URLs
 * @param insertionColumn - Column index to insert the work item into (default 0)
 * @returns Updated array of tableRow ADF nodes
 */
const updateRowsWithWorkItems = (
	rows: ADFEntity[],
	workItemsByRow: Map<number, string>,
	insertionColumn: number = 0,
): ADFEntity[] | null => {
	const tableRows: ADFEntity[] = rows.slice();

	// Find the next insertable tableCell starting from insertionColumn
	const col = adjustInsertionColumn(tableRows, workItemsByRow, insertionColumn);
	if (col === -1) {
		return null;
	}

	for (const [rowIndex, workItemUrl] of workItemsByRow) {
		const row = tableRows[rowIndex];
		if (!row || row.type !== 'tableRow' || !row.content) {
			return null;
		}

		if (col >= row.content.length) {
			return null;
		}

		const targetCellAdf = row.content[col];
		if (!targetCellAdf) {
			return null;
		}
		const updatedCell = constructTableCellWithWorkItem(targetCellAdf, workItemUrl);
		if (!updatedCell) {
			return null;
		}

		// Update the row with the new cell that includes the work item
		tableRows[rowIndex] = {
			...row,
			content: [...row.content.slice(0, col), updatedCell, ...row.content.slice(col + 1)],
		};
	}

	return tableRows;
};

/**
 * Constructs an entire ADF table node with work items inserted into corresponding cells
 *
 * @param table - The HTML table element
 * @param workItemsByRow - Map of row indices to work item URLs
 * @param insertionColumn - Optional column index to insert work item, default set to 0 for now
 * @returns Complete table ADF node with work items inserted into insertionColumn or null if conversion fails
 */
export const constructTableWithWorkItems = (
	table: HTMLTableElement,
	workItemsByRow: Map<number, string>,
	insertionColumn: number = 0,
): ADFEntity | null => {
	try {
		const tableAdf = htmlToADF(table.outerHTML);
		if (!tableAdf || !tableAdf.content || tableAdf.content.length === 0) {
			return null;
		}

		// Extract the table node from the ADF and return null if no table or no rows
		const tableNode: any = tableAdf.content.find((node: any) => node.type === 'table');
		if (!tableNode || !tableNode.content) {
			return null;
		}

		// Determine if the first row is a header and apply an offset if so
		const firstRow = tableNode.content?.[0];
		const startColumnIndex = 0;
		const hasHeaderRow =
			Array.isArray(firstRow?.content) &&
			isHeaderRowCells(firstRow.content.slice(startColumnIndex));
		const rowOffset = hasHeaderRow ? 1 : 0;

		// Apply row offset so rowIndex from issues mapping accounts for header row if present
		const offsetWorkItemsByRow: Map<number, string> = new Map();
		for (const [rowIndex, url] of workItemsByRow) {
			offsetWorkItemsByRow.set(rowIndex + rowOffset, url);
		}

		// Update applicable rows with matching work items
		const updatedRows = updateRowsWithWorkItems(
			tableNode.content,
			offsetWorkItemsByRow,
			insertionColumn,
		);

		// Failure to update rows should propagate as null to signal failure to callers
		if (!updatedRows) {
			return null;
		}

		// If no updates were applied, abandon insertion entirely
		if (updatedRows === tableNode.content) {
			return null;
		}

		return {
			...tableNode,
			content: updatedRows,
		};
	} catch (error) {
		return null;
	}
};
